/**
 * Enum Value Formatters
 *
 * Utilities for formatting enum values in dropdowns and cells.
 * Converts camelCase values to user-friendly Title Case display.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  PlanOptionNameEnum,
} from "@platform/openapi-types";
import startCase from "lodash/startCase";

import type { BenefitValue } from "../types/values.types";

/**
 * Display order for the Plan Level dropdown and any other UI offering the
 * same choices — the single source of truth for Low → Medium → High → Not
 * Applicable.
 */
export const PLAN_LEVEL_ORDER: PlanOptionNameEnum[] = [
  PlanOptionNameEnum.low,
  PlanOptionNameEnum.medium,
  PlanOptionNameEnum.high,
  PlanOptionNameEnum.notApplicable,
];

/**
 * Default mapping for acronyms that should stay uppercase
 */
const DEFAULT_ACRONYM_MAP: Record<string, string> = {
  ucr: "UCR",
  mac: "MAC",
  mpa: "MPA",
  ad: "AD",
  dd: "DD",
  adnd: "AD&D",
  eoi: "EOI",
  icu: "ICU",
  ppo: "PPO",
  hmo: "HMO",
  dhmo: "DHMO",
  pepm: "PEPM",
};

/**
 * Format an enum value for display to users
 *
 * @param value - The camelCase enum value (e.g., "dollarAmount", "45thUcr")
 * @param customLabels - Optional full override map (e.g., { "notIncluded": "N/A" })
 * @param acronymMap - Optional custom acronym replacements (merges with defaults)
 * @returns Formatted display string (e.g., "Dollar Amount", "45th UCR")
 *
 * @example
 * formatEnumValue("dollarAmount") // "Dollar Amount"
 * formatEnumValue("45thUcr") // "45th UCR"
 * formatEnumValue("percentageAmount") // "Percentage Amount"
 * formatEnumValue("notIncluded", { notIncluded: "N/A" }) // "N/A"
 */
export const formatEnumValue = (
  value?: string,
  customLabels?: Record<string, string>,
  acronymMap?: Record<string, string>,
): string => {
  // Return empty string for null/undefined
  if (!value) return "";

  // Check for full override first
  if (customLabels && value in customLabels) {
    return customLabels[value];
  }

  // Merge custom acronyms with defaults
  const acronyms = { ...DEFAULT_ACRONYM_MAP, ...acronymMap };

  // Apply startCase to convert camelCase to Title Case
  let formatted = startCase(value);

  // Replace acronyms with uppercase versions
  Object.entries(acronyms).forEach(([word, acronym]) => {
    const regex = new RegExp(`\\b${word}\\b`, "gi");
    formatted = formatted.replace(regex, acronym);
  });

  return formatted;
};

/**
 * Create a formatValue function for AG Grid cellEditorParams
 *
 * This returns a function that can be used directly in cellEditorParams.formatValue
 * for agRichSelectCellEditor to format dropdown options.
 *
 * @param customLabels - Optional full override map
 * @param acronymMap - Optional custom acronym replacements
 * @returns A function that formats enum values for display
 *
 * @example
 * // In cell.configurations.ts:
 * cellEditorParams: {
 *   values: ["dollarAmount", "percentageAmount", "notIncluded"],
 *   formatValue: createEnumFormatter(),
 * }
 */
export const createEnumFormatter =
  (
    customLabels?: Record<string, string>,
    acronymMap?: Record<string, string>,
  ) =>
  (value: string): string =>
    formatEnumValue(value, customLabels, acronymMap);

/**
 * Create a valueFormatter function for AG Grid ColDef
 *
 * This returns a function that can be used directly in ColDef.valueFormatter
 * to format the displayed cell value (when not editing).
 *
 * @param customLabels - Optional full override map
 * @param acronymMap - Optional custom acronym replacements
 * @returns A valueFormatter function compatible with AG Grid
 *
 * @example
 * // In cell.configurations.ts:
 * {
 *   cellEditorParams: {
 *     values: ["dollarAmount", "percentageAmount"],
 *     formatValue: createEnumFormatter(),
 *   },
 *   valueFormatter: createValueFormatter(),
 * }
 */
export const createValueFormatter =
  (
    customLabels?: Record<string, string>,
    acronymMap?: Record<string, string>,
  ) =>
  (params: { value?: unknown }): string => {
    if (!params.value) return "-";

    // Handle object values by extracting the string representation
    let stringValue: string;
    if (typeof params.value === "object") {
      // Try to extract a meaningful string from the object
      const obj = params.value as BenefitValue;
      if ("type" in obj && typeof obj.type === "string") {
        stringValue = obj.type;
      } else if ("value" in obj && typeof obj.value === "string") {
        stringValue = obj.value;
      } else {
        // No extractable value found, return empty string
        return "";
      }
    } else {
      stringValue = String(params.value);
    }

    return formatEnumValue(stringValue, customLabels, acronymMap);
  };

/**
 * Formats policy period for display in column headers
 *
 * @param policyPeriod - Raw policy period value from BaseBenefitPlanPolicyPeriod enum
 * @param nickname - Optional user-defined nickname
 * @param alternateIndex - For alternate plans without nicknames, the 1-based index
 * @param isHistorical - When true, suffixes the label with " (Previous)" to mark past versions
 * @returns Formatted display name
 *
 * @example
 * formatPolicyPeriodDisplay(BaseBenefitPlanPolicyPeriod.newBusiness) // "New Business"
 * formatPolicyPeriodDisplay(BaseBenefitPlanPolicyPeriod.current) // "Current"
 * formatPolicyPeriodDisplay(BaseBenefitPlanPolicyPeriod.alternate, undefined, 2) // "Alternate 2"
 * formatPolicyPeriodDisplay(BaseBenefitPlanPolicyPeriod.alternate, "Custom Plan") // "Alternate - Custom Plan"
 * formatPolicyPeriodDisplay(BaseBenefitPlanPolicyPeriod.current, "Current") // "Current"
 * formatPolicyPeriodDisplay(undefined, "Guardian Accident Standard", undefined, true) // "Guardian Accident Standard (Previous)"
 */
export const formatPolicyPeriodDisplay = (
  policyPeriod?: BaseBenefitPlanPolicyPeriod,
  nickname?: string,
  alternateIndex?: number,
  isHistorical?: boolean,
): string => {
  const label = buildPolicyPeriodLabel(policyPeriod, nickname, alternateIndex);
  // Historical columns get a " (Previous)" suffix so the version distinction
  // is visible in the header without needing extra UI chrome. Applied here on
  // the final label — not by mutating the group's nickname — so the raw
  // nickname stays intact for dialog prefill and persistence.
  return isHistorical ? `${label} (Previous)` : label;
};

const buildPolicyPeriodLabel = (
  policyPeriod?: BaseBenefitPlanPolicyPeriod,
  nickname?: string,
  alternateIndex?: number,
): string => {
  if (!policyPeriod) {
    // No policy period (e.g. proposal-derived columns) — the nickname IS the
    // full label, so return it directly without adding a period prefix.
    return nickname ?? "Plan Group";
  }

  let basePeriodLabel: string;
  switch (policyPeriod) {
    case BaseBenefitPlanPolicyPeriod.newBusiness:
      basePeriodLabel = "New Business";
      break;
    case BaseBenefitPlanPolicyPeriod.current:
      basePeriodLabel = "Current";
      break;
    case BaseBenefitPlanPolicyPeriod.alternate:
      basePeriodLabel = alternateIndex
        ? `Alternate ${alternateIndex}`
        : "Alternate";
      break;
    default:
      basePeriodLabel = policyPeriod;
  }

  if (!nickname) {
    return basePeriodLabel;
  }

  // When the nickname matches the base period label, show just the nickname.
  // Otherwise, prefix with the base label so context is preserved.
  if (nickname === basePeriodLabel) {
    return nickname;
  }

  return `${basePeriodLabel} - ${nickname}`;
};

/**
 * Checks if a search term matches the start of any word in the display text.
 * Case-insensitive. Words are split on whitespace.
 *
 * @example
 * matchesWordStart("pe", "Percentage")   // true  — "pe" starts "Percentage"
 * matchesWordStart("pe", "PEPM")         // true  — "pe" starts "PEPM"
 * matchesWordStart("pe", "Heaped")       // false — "pe" is mid-word
 * matchesWordStart("funded", "Self Funded") // true — "funded" starts "Funded"
 */
export const matchesWordStart = (
  search: string,
  displayText: string,
): boolean => {
  const searchLower = search.toLowerCase();
  return displayText
    .split(/\s+/)
    .some((word) => word.toLowerCase().startsWith(searchLower));
};

/**
 * Creates a values callback for agRichSelectCellEditor that filters using
 * word-start matching on formatted display values.
 *
 * Used with `filterListAsync: true` so AG Grid delegates filtering to this
 * callback instead of using the built-in searchType algorithm. Returns a
 * resolved Promise so filtering is synchronous in practice.
 *
 * @param allValues - The complete list of raw enum values
 * @param formatValue - Function to convert raw values to display text
 * @returns A callback suitable for cellEditorParams.values
 */
export const createWordStartFilter =
  (
    allValues: string[],
    formatValue: (value: string) => string,
  ): ((params: { search?: string }) => Promise<string[]>) =>
  (params) => {
    const search = params.search ?? "";
    if (!search) {
      return Promise.resolve(allValues);
    }
    return Promise.resolve(
      allValues.filter((value) => matchesWordStart(search, formatValue(value))),
    );
  };
